import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  CalendarClock,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  FilePlus,
  FileUp,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  RotateCcw,
  Trash2,
  Truck,
  UserRound,
  UserX,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { ErrorState } from "@/components/ui/states";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CampoData from "@/components/common/CampoData";
import ArchivioDocumenti from "@/components/documenti/ArchivioDocumenti";
import { AssistenzeCliente } from "@/components/assistenza/Assistenze";
import { ModificaFontiDialog } from "@/components/crm/FontiScelta";
import AggiornaPercorso from "@/components/crm/AggiornaPercorso";
import { EliminaClienteDialog } from "@/components/crm/EliminaCliente";
import FattoFuori from "@/components/crm/FattoFuori";
import { LavoroMontaggio, OrganizzaMontaggioDialog } from "@/components/crm/Montaggio";
import PersoDialog, { fraTreMesi } from "@/components/crm/PersoDialog";
import { NuovoAppuntamentoDialog } from "@/pages/CalendarioPage";
import { IncassoDialog } from "@/pages/FattureIncassiPage";
import { vociDi } from "@/lib/accessi";
import { eur } from "@/lib/formato";
import { FIRMATI, oggiRoma } from "@/lib/soldi";
import {
  TIPO_APPUNTAMENTO,
  annoDellaSettimana,
  daSegnare,
  erroreDi,
  giorniDellaSettimana,
  giorno,
  giornoCorto,
  giornoOra,
  lunediDellaSettimana,
  settimanaDi,
  statoAppuntamento,
  statoCommerciale,
} from "@/lib/crm";
import { cn } from "@/lib/utils";

/* La scheda cliente col layout del pacchetto (tavole U-scheda-1440/390): la testata col valore del lavoro,
   «Adesso» e «Poi» in cima, il percorso dalla prima visita al montaggio, e di lato contatti, WhatsApp,
   la landing e i documenti. Dati, gesti e permessi sono quelli di prima: cambia dove stanno. */

const TONO_PILLOLA = { attenzione: "warning", errore: "danger", info: "brand", ok: "success", neutro: "neutral" };

/* ── Il percorso ────────────────────────────────────────────────────────────
   Le tappe del cliente dalla prima visita alla garanzia, una per riga, con la data a sinistra.
   Pieno = fatto, anello = in agenda, vuoto = non ancora: la forma dice lo stato insieme al
   colore, mai il colore da solo. I gruppi (Vendita, Produzione, Consegna, Chiusura) cominciano
   dove comincia la loro prima tappa: l'ordine resta quello del server. */
function Pallino({ stato }) {
  if (stato === "fatto") return <span className="relative z-10 block size-3.5 shrink-0 rounded-full bg-brand ring-4 ring-surface" aria-hidden />;
  if (stato === "in_arrivo")
    return <span className="relative z-10 block size-3.5 shrink-0 rounded-full border-2 border-brand bg-surface ring-4 ring-surface" aria-hidden />;
  return <span className="relative z-10 block size-3.5 shrink-0 rounded-full border-2 border-line-input bg-surface ring-4 ring-surface" aria-hidden />;
}

const LEGGI_STATO = { fatto: "fatto", in_arrivo: "in agenda", spento: "non ancora" };
const GRUPPO_DA = { ingresso: "Vendita", rilievo: "Produzione", consegna: "Consegna", garanzia: "Chiusura" };
const eRata = (p) => p.codice === "acconto" || p.codice.startsWith("rata-");

/* I gesti sugli ordini (0040, 0046): dove si aprono, cosa dicono, dove scrivono. La merce la
   segnano titolare e amministrazione; ordine e conferma anche chi ha venduto il contratto.
   Chi può lo decide il backend (`azione` sulla tappa) e lo ridice il database. */
const GESTI = {
  segna_merce: {
    tappa: "merce", rotta: "merce-arrivata", titolo: "Merce arrivata", etichetta: "Arrivata il", fatto: "Merce arrivata in magazzino: segnata",
    descrizione: "Il giorno in cui la merce del contratto è arrivata in magazzino. Vale per tutti gli ordini del contratto.",
    bottone: (p) => (p.stato === "fatto" ? ["Cambia data", "Cambia la data di arrivo della merce"] : ["Segna arrivata", "Segna la merce arrivata"]),
  },
  segna_ordine: {
    tappa: "ordine", rotta: "ordine", titolo: "Ordine al fornitore", etichetta: "Ordinato il", fatto: "Ordine segnato",
    descrizione: "Il giorno in cui è partito l'ordine al fornitore. Vale per tutti gli ordini del contratto.",
    bottone: (p) => (p.quando ? ["Cambia data", "Cambia la data dell'ordine"] : ["Segna ordine", "Segna l'ordine al fornitore"]),
  },
  segna_conferma: {
    tappa: "conferma", rotta: "conferma-ordine", titolo: "Conferma d'ordine", etichetta: "Arrivata il", fatto: "Conferma d'ordine segnata",
    descrizione: "Il giorno in cui è arrivata la conferma del fornitore e la settimana di arrivo che c'è scritta.",
    bottone: (p) => (p.quando ? ["Cambia", "Cambia la conferma d'ordine"] : ["Segna conferma", "Segna la conferma d'ordine"]),
  },
};

function LineaDelTempo({ passi, onGesto, onIncasso, onAggiorna }) {
  return (
    <section className="rounded-xl border border-line bg-surface p-4 md:p-card" data-testid="linea-del-tempo">
      <div className="flex flex-wrap items-start justify-between gap-group">
        <div>
          <h2 className="font-display text-card-title text-ink-strong">Il percorso</h2>
          <p className="text-small text-ink-2">dalla prima visita alla garanzia</p>
        </div>
        {onAggiorna ? (
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-9" onClick={onAggiorna} data-testid="percorso-aggiorna">
            Aggiorna percorso
          </Button>
        ) : null}
      </div>
      <ol className="mt-group grid" aria-label="Le tappe del cliente">
        {passi.map((p, i) => {
          const gesto = GESTI[p.azione];
          const [testo, descrizione] = gesto ? gesto.bottone(p) : [];
          const gruppo = GRUPPO_DA[p.codice];
          const ultima = i === passi.length - 1;
          return (
            <li key={p.codice} data-testid={`tappa-${p.codice}`} data-stato={p.stato}>
              {gruppo ? (
                <p className={cn("ml-[calc(4.5rem+28px)] pb-2 text-caption text-ink-2 md:ml-[calc(5.5rem+28px)]", i > 0 && "pt-3")}>{gruppo}</p>
              ) : null}
              <div className="grid grid-cols-[4.5rem_14px_minmax(0,1fr)] gap-x-3.5 md:grid-cols-[5.5rem_14px_minmax(0,1fr)]">
                <span className={cn("pt-0.5 text-right text-small tabular-nums", p.stato === "spento" ? "text-ink-3" : "text-ink-2")}>
                  {p.quando ? giornoCorto(p.quando) : ""}
                </span>
                <span className="relative flex justify-center pt-1">
                  {!ultima ? <span className="absolute bottom-0 top-1 w-0.5 bg-line" aria-hidden /> : null}
                  <Pallino stato={p.stato} />
                </span>
                <div className="min-w-0 pb-4 break-words">
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={cn(p.stato === "spento" ? "text-ink-2" : "font-semibold text-ink-strong")}>{p.titolo}</span>
                    <span className="sr-only">: {p.a_mano?.esito === "saltato" ? "non c'è stato" : LEGGI_STATO[p.stato]}</span>
                    {p.stato === "fatto" ? (
                      <>
                        <StatusBadge tone="success" aria-hidden>
                          {eRata(p) && !p.a_mano ? "Incassato" : "Fatto"}
                        </StatusBadge>
                        {p.a_mano ? (
                          <StatusBadge tone="neutral" aria-hidden data-testid={`tappa-${p.codice}-a-mano`}>
                            A mano
                          </StatusBadge>
                        ) : null}
                      </>
                    ) : p.stato === "in_arrivo" ? (
                      <StatusBadge tone="brand" aria-hidden>
                        In agenda
                      </StatusBadge>
                    ) : null}
                  </p>
                  {p.nota ? <p className="text-small text-ink-2">{p.nota}</p> : null}
                  {gesto ? (
                    <button
                      type="button"
                      onClick={(e) => onGesto(p.azione, e.currentTarget)}
                      aria-label={descrizione}
                      className="inline-flex min-h-11 items-center text-left text-small font-semibold text-brand-ink hover:underline md:min-h-8"
                      data-testid={`tappa-${gesto.tappa}-segna`}
                    >
                      {testo}
                    </button>
                  ) : p.azione === "segna_incasso" ? (
                    <button
                      type="button"
                      onClick={() => onIncasso(p.rata)}
                      aria-label={`${p.codice === "rata-saldo" ? "Segna il saldo" : "Segna l'incasso"}: ${p.titolo}`}
                      className="inline-flex min-h-11 items-center text-left text-small font-semibold text-brand-ink hover:underline md:min-h-8"
                      data-testid={`tappa-${p.codice}-incasso`}
                    >
                      {p.codice === "rata-saldo" ? "Segna saldo" : "Segna incasso"}
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function GestoOrdineDialog({ gesto, passi, onClose, clienteId, onFatto }) {
  const g = gesto ? GESTI[gesto.azione] : null;
  const tappa = gesto ? passi.find((p) => p.codice === g.tappa) : null;
  const ordinato = passi.find((p) => p.codice === "ordine")?.quando?.slice(0, 10);
  const [data, setData] = useState("");
  const [settimana, setSettimana] = useState("");
  const [anno, setAnno] = useState(null); // null = lo deduce la settimana
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!tappa) return;
    setData(tappa.quando ? tappa.quando.slice(0, 10) : oggiISO());
    const scritta = tappa.settimana_arrivo ? settimanaDi(tappa.settimana_arrivo) : null;
    setSettimana(scritta ? String(scritta.numero) : "");
    setAnno(scritta ? scritta.anno : null);
  }, [tappa]);
  const conferma = gesto?.azione === "segna_conferma";
  const n = /^\d{1,2}$/.test(settimana.trim()) ? Number(settimana) : null;
  const annoVero = anno ?? (n && data ? annoDellaSettimana(n, data) : null);
  const lunedi = n && annoVero ? lunediDellaSettimana(n, annoVero) : null;
  const giorni = !conferma || !settimana.trim()
    ? null
    : lunedi
      ? `Settimana ${n}: ${giorniDellaSettimana(lunedi)}.`
      : `Il ${annoVero || "…"} non ha la settimana ${settimana}.`;

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/crm/clienti/${clienteId}/${g.rotta}`, conferma ? { data, settimana: n, anno: annoVero } : { data });
      onClose();
      onFatto(g.fatto);
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };
  const id = g?.tappa;
  return (
    <Dialog open={Boolean(gesto)} onOpenChange={(aperto) => (aperto ? null : onClose())}>
      {/* Aperto senza DialogTrigger: il fuoco, chiuso il dialogo, si riporta a mano sul bottone. */}
      <DialogContent
        data-testid={`${id}-modal`}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (gesto?.ritorno?.isConnected) gesto.ritorno.focus();
        }}
      >
        {g ? (
          <form onSubmit={salva} className="grid gap-card">
            <DialogHeader>
              <DialogTitle>{g.titolo}</DialogTitle>
              <DialogDescription>{g.descrizione}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-inside">
              <Label htmlFor={`${id}-data`}>{g.etichetta}</Label>
              <CampoData
                id={`${id}-data`}
                value={data}
                min={conferma ? ordinato : undefined}
                max={oggiISO()}
                onChange={(e) => setData(e.target.value)}
                testId={`${id}-data`}
                required
              />
            </div>
            {conferma ? (
              <div className="grid gap-inside">
                <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-group">
                  <div className="grid content-start gap-inside">
                    <Label htmlFor="conferma-settimana">Settimana di arrivo</Label>
                    <Input
                      id="conferma-settimana"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="es. 47"
                      value={settimana}
                      onChange={(e) => setSettimana(e.target.value)}
                      aria-describedby="conferma-giorni"
                      data-testid="conferma-settimana"
                      required
                    />
                  </div>
                  <div className="grid content-start gap-inside">
                    <Label htmlFor="conferma-anno">Anno</Label>
                    <Input
                      id="conferma-anno"
                      inputMode="numeric"
                      autoComplete="off"
                      value={annoVero ?? ""}
                      onChange={(e) => setAnno(/^\d{4}$/.test(e.target.value) ? Number(e.target.value) : e.target.value)}
                      data-testid="conferma-anno"
                    />
                  </div>
                </div>
                <p id="conferma-giorni" aria-live="polite" className={cn("text-xs", lunedi ? "text-muted-foreground" : "text-warning-foreground")} data-testid="conferma-giorni">
                  {giorni || "Come la scrive il fornitore: «settimana 47»."}
                </p>
              </div>
            ) : null}
            <DialogFooter className="flex-col-reverse gap-inside sm:flex-row">
              <Button type="button" variant="outline" onClick={onClose}>
                Annulla
              </Button>
              <Button type="submit" disabled={busy || !data || (conferma && !lunedi)} data-testid={`${id}-salva`}>
                {busy ? "Salvo…" : "Salva"}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/* ── I dati in cima ─────────────────────────────────────────────────────── */
/* Una riga etichetta · valore: a sinistra cosa è, a destra il dato (la tavola «Dalla landing»). */
function Dato({ etichetta, children, testId }) {
  return (
    <div className="grid min-w-0 grid-cols-[8.5rem_minmax(0,1fr)] gap-group py-1" data-testid={testId}>
      <dt className="text-small text-ink-2">{etichetta}</dt>
      <dd className="break-words text-small text-ink">{children || "—"}</dd>
    </div>
  );
}

function valoreDelLavoro(scheda) {
  // Firmato = lo stato, come il cruscotto e la linea del tempo (0030): la bozza nata
  // dal Confermo non è un contratto.
  const firmato = (scheda.documenti.contratti || []).find((k) => FIRMATI.has(k.stato) && k.totale);
  if (firmato) return { testo: eur(firmato.totale, { decimali: 0 }), nota: "contratto" };
  if (scheda.cliente.valore_stimato !== null && scheda.cliente.valore_stimato !== undefined) {
    return { testo: eur(scheda.cliente.valore_stimato, { decimali: 0 }), nota: scheda.cliente.storico_cliente ? "venduto" : "stimato" };
  }
  return null;
}

/* ── La prossima azione, scritta a mano (D5) ─────────────────────────────── */
function oggiISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function ProssimaAzioneDialog({ open, onOpenChange, onFatto, cliente }) {
  const [data, setData] = useState("");
  const [testo, setTesto] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setData(cliente.prossima_azione_at ? cliente.prossima_azione_at.slice(0, 10) : oggiISO());
      setTesto(cliente.prossima_azione || "");
    }
  }, [open, cliente]);
  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      /* Si manda il giorno: l'ora (le 9 di Roma, l'apertura dello showroom) la
         fissa il server, che conosce il fuso anche d'inverno. */
      await api.patch(`/crm/clienti/${cliente.id}`, {
        prossima_azione_giorno: data,
        prossima_azione: testo.trim() || "Richiamare",
      });
      onOpenChange(false);
      onFatto("Prossima azione salvata");
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };
  const togli = async () => {
    setBusy(true);
    try {
      await api.patch(`/crm/clienti/${cliente.id}`, { togli_prossima_azione: true });
      onOpenChange(false);
      onFatto("Prossima azione tolta");
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="prossima-azione-modal">
        <form onSubmit={salva} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>Prossima azione</DialogTitle>
            <DialogDescription>Cosa fare e quando. Il giorno scelto il cliente sale in cima a «Cosa succede adesso».</DialogDescription>
          </DialogHeader>
          <div className="grid gap-card">
            <div className="grid gap-inside">
              <Label htmlFor="pa-testo">Cosa</Label>
              <Input
                id="pa-testo"
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                placeholder="Richiamare per la seconda visita"
                data-testid="prossima-azione-testo"
              />
            </div>
            <div className="grid gap-inside">
              <Label htmlFor="pa-data">Quando</Label>
              <CampoData id="pa-data" value={data} onChange={(e) => setData(e.target.value)} testId="prossima-azione-data" required />
            </div>
          </div>
          <DialogFooter className="gap-inside sm:justify-between">
            {cliente.prossima_azione_at ? (
              <Button type="button" variant="ghost" onClick={togli} disabled={busy}>
                Togli
              </Button>
            ) : (
              <span />
            )}
            <div className="flex flex-col-reverse gap-inside sm:flex-row">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={busy || !data} data-testid="prossima-azione-salva">
                {busy ? "Salvo…" : "Salva"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── I riquadri ──────────────────────────────────────────────────────────── */
function Riquadro({ titolo, sotto, children, testId, azione }) {
  return (
    <section className="rounded-xl border border-line bg-surface p-4 md:p-card" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-group">
        <h2 className="font-display text-card-title text-ink-strong">{titolo}</h2>
        {azione}
      </div>
      {sotto ? <p className="mt-micro text-small text-ink-2">{sotto}</p> : null}
      <div className="mt-group">{children}</div>
    </section>
  );
}

/* «Adesso» e «Poi» (decisione del pacchetto): le cose da fare della scheda, la più urgente per prima,
   con il gesto accanto. Sono quelle di «Cosa succede adesso» del server, al massimo tre. */
function Cosa({ c, etichetta, onGesto }) {
  const ritardo = c.urgenza === 0;
  return (
    <li className="flex gap-3" data-testid="adesso-voce" data-urgenza={c.urgenza}>
      <span
        className={cn(
          "inline-flex size-10 shrink-0 items-center justify-center rounded-md",
          ritardo ? "bg-warning-bg text-warning-ink" : "bg-surface text-brand-ink",
        )}
        aria-hidden
      >
        {ritardo ? <AlertCircle className="size-5" /> : <CalendarClock className="size-5" />}
      </span>
      <div className="min-w-0 flex-1">
        {etichetta ? <p className="text-caption text-brand-ink">{etichetta}</p> : null}
        <p className="font-bold text-ink-strong">
          {c.testo}
          {ritardo ? <span className="sr-only"> (in ritardo)</span> : null}
        </p>
        {c.quando || c.nota ? <p className="text-small text-ink-2">{[c.quando ? giornoOra(c.quando) : null, c.nota].filter(Boolean).join(" · ")}</p> : null}
        <div className="flex flex-wrap gap-inside">
          {GESTI[c.azione] ? (
            <Button variant="outline" size="sm" className="mt-inside h-11 md:h-9" onClick={(e) => onGesto(c.azione, e.currentTarget)} data-testid={`adesso-${c.azione}`}>
              {c.azione === "segna_ordine" ? "Segna ordine" : "Segna conferma d'ordine"}
            </Button>
          ) : null}
          {c.appuntamento_id ? (
            <Button asChild variant="outline" size="sm" className="mt-inside h-11 md:h-9">
              <Link to={`/calendario?appuntamento=${c.appuntamento_id}`}>Apri l'appuntamento</Link>
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function AdessoPoi({ cose, onGesto }) {
  if (!cose.length)
    return (
      <section className="rounded-xl bg-brand-subtle px-4 py-3 text-ink-2 md:px-card" data-testid="cliente-adesso">
        Niente in sospeso. Quando c'è un passo da fare, compare qui.
      </section>
    );
  const [adesso, ...poi] = cose;
  return (
    <section className="grid gap-group rounded-xl bg-brand-subtle p-4 md:grid-cols-2 md:p-card" data-testid="cliente-adesso" aria-label="Cosa succede adesso">
      <ul>
        <Cosa c={adesso} etichetta="Adesso" onGesto={onGesto} />
      </ul>
      {poi.length ? (
        <ul className="grid gap-group border-t border-brand-soft pt-group md:border-l md:border-t-0 md:pl-card md:pt-0">
          {poi.map((c, i) => (
            <Cosa key={i} c={c} etichetta={i === 0 ? "Poi" : null} onGesto={onGesto} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function Appuntamenti({ appuntamenti }) {
  if (!appuntamenti.length) {
    return <p className="text-ink-2">Nessun appuntamento. Quelli prenotati da Cal arrivano qui da soli.</p>;
  }
  return (
    <ul className="-mx-inside grid">
      {appuntamenti.map((a) => {
        const s = daSegnare(a) ? { label: "Da segnare", pill: "attenzione" } : statoAppuntamento(a.stato, a.tipo);
        return (
          <li key={a.id}>
            <Link
              to={`/calendario?appuntamento=${a.id}`}
              data-testid={`appuntamento-link-${a.id}`}
              className="flex min-h-12 items-center gap-group rounded-sm px-inside py-2 transition-colors duration-150 ease-enter hover:bg-surface-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-ink-strong">{TIPO_APPUNTAMENTO[a.tipo] || a.tipo}</p>
                <p className="text-small text-ink-2">
                  {giornoOra(a.data_inizio)}
                  {a.sede ? ` · ${a.sede}` : ""}
                </p>
              </div>
              <StatusBadge tone={TONO_PILLOLA[s.pill]}>{s.label}</StatusBadge>
              <ChevronRight className="size-4 shrink-0 text-ink-3" aria-hidden />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/* ── «Dalla landing»: quello che la persona ha scritto prenotando ─────────────
   Le risposte del modulo di Cal come sono arrivate (wa_bookings: le legge chi vede
   il cliente, 0037), la provenienza e i consensi con la loro data. Anche se poi
   qualcuno ha corretto il nome o il telefono nella scheda, qui resta l'originale. */
const VOCI_CAMPAGNA = [
  ["utm_source", "Sorgente"],
  ["utm_medium", "Mezzo"],
  ["utm_campaign", "Campagna"],
  ["utm_content", "Inserzione"],
  ["utm_term", "Posizionamento"],
];

function DallaLanding({ prenotazioni, cliente }) {
  const [b, ...altre] = prenotazioni;
  const pv = cliente.provenienza || {};
  const campagna = VOCI_CAMPAGNA.filter(([k]) => pv[k]);
  const cookie = pv.consenso_cookie === "si" ? "sì" : pv.consenso_cookie === "no" ? "no" : null;
  const quandoCookie = pv.consenso_cookie_data || pv.consensi_letti_il;
  return (
    <Riquadro titolo="Dalla landing" sotto={b.created_at ? `Modulo compilato ${giornoOra(b.created_at)}` : null} testId="cliente-dalla-landing">
      {b.progetto || b.budget ? (
        <p className="mb-group rounded-sm bg-surface-2 px-3 py-2.5 text-ink" data-testid="landing-progetto">
          {[b.progetto ? `«${b.progetto}»` : null, b.budget ? `budget ${b.budget}` : null].filter(Boolean).join(" · ")}
        </p>
      ) : null}
      <dl className="grid">
        <Dato etichetta="Nome">{b.nome}</Dato>
        <Dato etichetta="Telefono">{b.telefono ? `+${b.telefono}` : null}</Dato>
        <Dato etichetta="Email">{b.email}</Dato>
        <Dato etichetta="Budget" testId="landing-budget">{b.budget}</Dato>
        <Dato etichetta="Sede">{b.sede}</Dato>
        <Dato etichetta="Appuntamento" testId="landing-appuntamento">
          {giornoOra(b.inizio)}
          {b.stato === "annullata" ? <span className="text-ink-2"> · annullato</span> : null}
        </Dato>
        <Dato etichetta="Da dove arriva" testId="landing-fonte">
          {cliente.fonte_principale || <span className="text-ink-2">non indicata</span>}
        </Dato>
        {campagna.map(([k, label]) => (
          <Dato key={k} etichetta={label}>
            <span className="break-all">{pv[k]}</span>
          </Dato>
        ))}
        <Dato etichetta="WhatsApp di marketing" testId="landing-consenso-wa">
          {cliente.consenso_wa_marketing_at
            ? `sì, il ${giornoOra(cliente.consenso_wa_marketing_at)}`
            : pv.consenso_whatsapp_marketing === true
              ? `ritirato (sì il ${giornoOra(pv.consensi_letti_il)})`
              : `no${pv.consensi_letti_il ? `, il ${giornoOra(pv.consensi_letti_il)}` : ""}`}
        </Dato>
        <Dato etichetta="Cookie della landing" testId="landing-consenso-cookie">
          {cookie ? `${cookie}${quandoCookie ? `, il ${giornoOra(quandoCookie)}` : ""}` : null}
        </Dato>
      </dl>
      {altre.length ? (
        <p className="mt-group text-small text-ink-2">
          {altre.length === 1 ? "Un'altra prenotazione" : `Altre ${altre.length} prenotazioni`}: {altre.map((x) => giornoOra(x.inizio)).join(" · ")}
        </p>
      ) : null}
    </Riquadro>
  );
}

function Scheletro() {
  return (
    <div className="grid gap-card" aria-busy="true" aria-label="Caricamento scheda">
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
      <div className="grid gap-card lg:grid-cols-[minmax(0,1fr)_22.5rem]">
        <Skeleton className="h-96 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    </div>
  );
}

/* Le rate del contratto in una barra, sotto il valore del lavoro: pagata = piena, da incassare = vuota.
   Solo a chi vede gli incassi (titolare e amministrazione): al venditore le rate restano spente anche se
   pagate (la RLS non gli dà gli incassi), e una barra vuota gli direbbe una cosa falsa. */
function BarraRate({ passi }) {
  const rate = passi.filter(eRata);
  if (!rate.length) return null;
  return (
    <ol className="mt-group grid gap-1.5" style={{ gridTemplateColumns: `repeat(${rate.length}, minmax(0, 1fr))` }} aria-label="Le rate" data-testid="cliente-rate">
      {rate.map((r) => (
        <li key={r.codice} className="min-w-0">
          <span className={cn("block h-2 rounded-full", r.stato === "fatto" ? "bg-brand" : "border border-dashed border-brand-soft bg-surface")} aria-hidden />
          <span className="mt-1.5 block truncate text-caption text-ink-strong">{r.titolo}</span>
          <span className="block truncate text-caption font-normal text-ink-2">
            {r.stato === "fatto" ? `incassato ${r.quando ? giornoCorto(r.quando) : ""}` : "da incassare"}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function ClienteSchedaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scheda, setScheda] = useState(null);
  const [errore, setErrore] = useState(null);
  const [perso, setPerso] = useState(false);
  const [persoBusy, setPersoBusy] = useState(false);
  const [azione, setAzione] = useState(false);
  const [fonti, setFonti] = useState(false);
  const [elimina, setElimina] = useState(false);
  const [gesto, setGesto] = useState(null);
  const [montaggio, setMontaggio] = useState(null);
  const [organizza, setOrganizza] = useState(false);
  const [preventivoBusy, setPreventivoBusy] = useState(false);
  const [fuori, setFuori] = useState(false);
  const [fuoriPasso, setFuoriPasso] = useState(null);
  const [percorso, setPercorso] = useState(false);
  const [incasso, setIncasso] = useState(null);
  const [visita, setVisita] = useState(null); // null = chiusa · {sedi, cal} = aperta (cal: undefined finché il calendario non risponde)
  const eliminaRef = useRef(null);
  const { profile } = useAuth() || {};
  const voci = vociDi(profile?.ruolo, profile?.sezioni_spente);

  const carica = useCallback(() => {
    api
      .get(`/crm/clienti/${id}`)
      .then((r) => {
        setScheda(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore({ status: e.response?.status, testo: erroreDi(e) }));
  }, [id]);

  useEffect(carica, [carica]);

  /* «Fissa visita» apre qui la stessa finestra del Calendario, col cliente già scelto. La finestra si azzera se il
     cliente cambia identità: quello passato è sempre lo stesso oggetto finché la scheda è la stessa. */
  const cliente = scheda?.cliente;
  const clienteVisita = useMemo(
    () => (cliente ? { id: cliente.id, nome: cliente.nome_visualizzato, telefono: cliente.telefono } : null),
    [cliente?.id, cliente?.nome_visualizzato, cliente?.telefono], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const apriVisita = () => {
    setVisita({ sedi: [], cal: undefined });
    const oggi = oggiRoma();
    api
      .get("/calendario", { params: { da: oggi, a: oggi } })
      .then((r) => setVisita((v) => (v ? { sedi: r.data.sedi || [], cal: Boolean(r.data.cal_dal_crm) } : v)))
      .catch((e) => {
        setVisita(null);
        toast.error(erroreDi(e));
      });
  };

  /* I lavori di consegna e montaggio (0047): si rileggono quando la scheda si ricarica. */
  useEffect(() => {
    if (!scheda) return undefined;
    let vivo = true; // la risposta di un cliente di prima non finisce sulla scheda di quello di adesso
    api
      .get(`/montaggio/cliente/${id}`)
      .then((r) => vivo && setMontaggio(r.data))
      .catch(() => vivo && setMontaggio(null));
    return () => {
      vivo = false;
    };
  }, [id, scheda]);

  const fatto = (msg) => {
    toast.success(msg);
    carica();
  };

  const ritiraConsenso = async () => {
    try {
      await api.patch(`/crm/clienti/${id}`, { ritira_consenso_wa: true });
      fatto("Consenso ritirato: niente più WhatsApp di marketing");
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };

  const riapri = async () => {
    try {
      await api.patch(`/crm/clienti/${id}`, { riapri: true });
      fatto("Cliente riaperto");
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };

  /* Perso col motivo; «da riprendere fra 3 mesi» è la prossima azione con quella data, nello stesso gesto. */
  const segnaPerso = async (motivo, riprendi) => {
    setPersoBusy(true);
    try {
      await api.patch(`/crm/clienti/${id}`, {
        segna_perso: true,
        perso_motivo: motivo,
        ...(riprendi ? { prossima_azione_giorno: fraTreMesi(), prossima_azione: "Risentirlo" } : {}),
      });
      setPerso(false);
      fatto(riprendi ? "Segnato perso: da riprendere fra 3 mesi" : "Segnato perso");
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setPersoBusy(false);
    }
  };

  /* «Nuovo preventivo»: nasce col cliente dentro, come dal «Nuovo cliente», e si apre il preventivatore. */
  const nuovoPreventivo = async () => {
    setPreventivoBusy(true);
    try {
      const p = await api.post("/preventivatore", { cliente_id: id, livello_ricarico_default: "M5" });
      navigate(`/preventivi/preventivatore/${p.data.id}`);
    } catch (err) {
      toast.error(erroreDi(err));
      setPreventivoBusy(false);
    }
  };

  const indietro = (
    <Link
      to="/clienti"
      data-testid="cliente-indietro"
      className="-ml-2 inline-flex h-11 items-center gap-1 justify-self-start rounded-sm px-2 font-semibold text-ink-2 transition-colors duration-150 ease-enter hover:bg-surface-3 hover:text-ink-strong md:h-9"
    >
      <ChevronLeft className="size-4" aria-hidden />
      Clienti
    </Link>
  );

  if (errore && !scheda) {
    const nonC = errore.status === 404;
    const quando = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date());
    return (
      <section className="grid gap-card" data-testid="page-cliente">
        {indietro}
        <div className="rounded-xl border border-line bg-surface" data-testid="cliente-non-trovato">
          <ErrorState
            icon={UserX}
            tone="neutral"
            className="py-section"
            title={nonC ? "Questa scheda non c'è" : "La scheda non si è caricata"}
            code={`CLI-${errore.status || "NET"} · ${quando}`}
          >
            {nonC ? (
              <>
                <p>Il cliente che cerchi è stato tolto, non è tra quelli che vedi, oppure il link che hai aperto è incompleto. Se ti serve, cercalo per nome o per telefono.</p>
                <form
                  role="search"
                  className="mx-auto mt-3 w-full max-w-sm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const q = new FormData(e.currentTarget).get("q")?.toString().trim();
                    navigate(q ? `/clienti?q=${encodeURIComponent(q)}` : "/clienti");
                  }}
                >
                  <Label htmlFor="cerca-cliente" className="sr-only">Nome, cognome o telefono</Label>
                  <Input id="cerca-cliente" name="q" type="search" placeholder="Nome, cognome o telefono" autoComplete="off" data-testid="cliente-non-trovato-cerca" />
                </form>
              </>
            ) : (
              <p>{errore.testo}</p>
            )}
            <Button asChild variant="secondary" className="mt-3">
              <Link to="/clienti">Torna ai clienti</Link>
            </Button>
          </ErrorState>
        </div>
      </section>
    );
  }
  if (!scheda) return <Scheletro />;

  const c = scheda.cliente;
  const s = statoCommerciale(scheda.stato?.stato);
  const apriGesto = (azione, ritorno) => setGesto({ azione, ritorno });
  const valore = valoreDelLavoro(scheda);
  const vedeIncassi = voci.includes("fatture-incassi");
  const puoFattoFuori = voci.includes("preventivatore") && ["titolare", "venditore"].includes(profile?.ruolo);
  const conversazione = scheda.conversazioni[0];
  const progetto = [c.progetto, c.venditore ? `la segue ${c.venditore}` : null, c.created_at ? `cliente dal ${giornoCorto(c.created_at)}` : null].filter(Boolean);

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-cliente">
      {indietro}
      {fuori ? (
        <FattoFuori
          cliente={{ id: c.id, nome: c.nome_visualizzato }}
          passo={fuoriPasso}
          onChiudi={() => setFuori(false)}
          onFatto={() => {
            setFuori(false);
            carica();
          }}
        />
      ) : null}
      <PersoDialog nome={c.nome_visualizzato} open={perso} onOpenChange={setPerso} onConferma={segnaPerso} busy={persoBusy} />
      {profile?.ruolo === "titolare" ? (
        <EliminaClienteDialog open={elimina} onOpenChange={setElimina} scheda={scheda} onSegnaPerso={() => setPerso(true)} onCambiata={carica} ritorno={eliminaRef} />
      ) : null}
      <ProssimaAzioneDialog open={azione} onOpenChange={setAzione} onFatto={fatto} cliente={c} />
      <ModificaFontiDialog open={fonti} onOpenChange={setFonti} onSalvato={carica} cliente={c} />
      <AggiornaPercorso
        open={percorso}
        onOpenChange={setPercorso}
        passi={scheda.linea_del_tempo}
        clienteId={c.id}
        puoFattoFuori={puoFattoFuori}
        onFatto={(nuova) => setScheda(nuova)}
        onFattoFuori={(passo) => {
          setPercorso(false);
          setFuoriPasso(passo);
          setFuori(true);
        }}
      />
      <IncassoDialog rata={incasso} onOpenChange={(v) => !v && setIncasso(null)} onErrore={carica} onSegnato={() => { setIncasso(null); carica(); }} />
      <NuovoAppuntamentoDialog
        open={Boolean(visita)}
        onOpenChange={(v) => !v && setVisita(null)}
        sedi={visita?.sedi || []}
        giornoIniziale={oggiRoma()}
        clienteIniziale={clienteVisita}
        calDalCrm={visita?.cal}
        onCreato={(app) => {
          fatto(app.da_cal ? "Fissata, anche su Cal: online quell'orario non si prenota più." : "Appuntamento fissato");
        }}
      />

      <header className="rounded-xl border border-line bg-surface" data-testid="cliente-testata">
        <div className="grid gap-card p-4 md:p-card lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
          <div className="min-w-0">
            <h1 className="text-balance break-words font-display text-h1-mobile text-ink-strong md:text-display">{c.nome_visualizzato}</h1>
            <div className="mt-group flex flex-wrap items-center gap-inside">
              <StatusBadge tone={TONO_PILLOLA[s.pill]} data-testid="cliente-stato">
                {s.label}
              </StatusBadge>
              {c.fonte ? <StatusBadge tone="brand">{c.fonte.split(" · ")[0]}</StatusBadge> : null}
              {c.sede ? <StatusBadge tone="neutral">{c.sede}</StatusBadge> : null}
            </div>
            {progetto.length ? <p className="mt-group text-ink-2">{progetto.join(" · ")}</p> : null}
            {c.storico_cliente ? (
              <p className="mt-inside text-ink-2" data-testid="cliente-storico">
                Cliente da prima del gestionale
              </p>
            ) : null}
            {c.perso_at ? (
              <p className="mt-inside font-semibold text-danger-ink" data-testid="cliente-perso">
                Perso il {giorno(c.perso_at)} · {c.perso_motivo}
              </p>
            ) : null}
          </div>
          <div className="min-w-0 border-t border-line-subtle pt-group lg:border-l lg:border-t-0 lg:pl-card lg:pt-0" data-testid="cliente-valore">
            <div className="flex items-baseline justify-between gap-group">
              <span className="text-ink-2">Valore del lavoro</span>
              <span className="font-figure text-h1 text-ink-strong">{valore ? valore.testo : "—"}</span>
            </div>
            {valore ? <p className="text-right text-caption font-normal text-ink-2">{valore.nota === "contratto" ? "dal contratto firmato" : valore.nota === "venduto" ? "venduto prima del gestionale" : "stimato"}</p> : null}
            {vedeIncassi && (scheda.documenti?.contratti || []).some((k) => FIRMATI.has(k.stato)) ? <BarraRate passi={scheda.linea_del_tempo} /> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-inside border-t border-line-subtle px-4 py-3 md:px-card">
          {voci.includes("calendario") ? (
            <Button onClick={apriVisita} data-testid="cliente-nuovo-appuntamento">
              <CalendarPlus />
              Fissa visita
            </Button>
          ) : null}
          {voci.includes("preventivatore") ? (
            <Button variant="outline" onClick={nuovoPreventivo} disabled={preventivoBusy} data-testid="btn-nuovo-preventivo">
              <FilePlus />
              {preventivoBusy ? "Creo…" : "Nuovo preventivo"}
            </Button>
          ) : null}
          {puoFattoFuori ? (
            <Button variant="outline" onClick={() => { setFuoriPasso(null); setFuori(true); }} data-testid="btn-fatto-fuori">
              <FileUp />
              Fatto fuori
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => setAzione(true)} data-testid="btn-prossima-azione">
            <CalendarClock />
            Prossima azione
          </Button>
          {c.perso_at ? (
            <Button variant="outline" onClick={riapri} data-testid="btn-riapri">
              <RotateCcw />
              Riapri
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setPerso(true)} data-testid="btn-segna-perso">
              <UserX />
              Segna perso
            </Button>
          )}
          <span className="flex flex-1 flex-wrap items-center justify-end gap-inside">
            {c.telefono ? (
              <Button asChild variant="outline" size="icon" className="size-11">
                <a href={`tel:${c.telefono_e164 || c.telefono}`} aria-label={`Chiama ${c.nome_visualizzato}`}>
                  <Phone />
                </a>
              </Button>
            ) : null}
            {conversazione ? (
              <Button asChild variant="outline" size="icon" className="size-11">
                <Link to={`/messaggi?contatto=${conversazione.wa_id}`} aria-label="Apri la conversazione WhatsApp">
                  <MessageCircle />
                </Link>
              </Button>
            ) : null}
            {c.email ? (
              <Button asChild variant="outline" size="icon" className="size-11">
                <a href={`mailto:${c.email}`} aria-label={`Scrivi a ${c.email}`}>
                  <Mail />
                </a>
              </Button>
            ) : null}
            {/* Meno in vista di proposito: è il gesto che non si annulla, e lo fa solo il titolare (0045). */}
            {profile?.ruolo === "titolare" ? (
              <Button
                ref={eliminaRef}
                variant="ghost"
                onClick={() => setElimina(true)}
                className="text-ink-2 hover:text-danger-ink"
                aria-label="Elimina cliente"
                data-testid="btn-elimina"
              >
                <Trash2 />
                <span className="hidden sm:inline">Elimina</span>
              </Button>
            ) : null}
          </span>
        </div>
      </header>

      <AdessoPoi cose={scheda.adesso} onGesto={apriGesto} />

      {montaggio?.puo_organizzare ? (
        <OrganizzaMontaggioDialog
          open={organizza}
          onOpenChange={setOrganizza}
          cliente={c}
          arrivo={scheda.linea_del_tempo.find((p) => p.codice === "conferma")?.settimana_arrivo || null}
          onFatto={carica}
        />
      ) : null}
      <GestoOrdineDialog gesto={gesto} passi={scheda.linea_del_tempo} onClose={() => setGesto(null)} clienteId={c.id} onFatto={fatto} />

      <div className="grid gap-card lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)]">
        <div className="grid content-start gap-card">
          <LineaDelTempo
            passi={scheda.linea_del_tempo}
            onGesto={apriGesto}
            onIncasso={setIncasso}
            onAggiorna={scheda.puo_aggiornare_percorso ? () => setPercorso(true) : null}
          />
          {montaggio && (montaggio.puo_organizzare || montaggio.lavori.length) ? (
            <Riquadro
              titolo="Consegna e montaggio"
              testId="cliente-montaggio"
              azione={
                montaggio.puo_organizzare ? (
                  <Button variant="outline" size="sm" className="h-11 md:h-9" onClick={() => setOrganizza(true)} data-testid="btn-organizza-montaggio">
                    <Truck />
                    Organizza consegna e montaggio
                  </Button>
                ) : null
              }
            >
              {montaggio.lavori.length ? (
                <div className="grid gap-group">
                  {montaggio.lavori.map((l) => (
                    <LavoroMontaggio key={l.lavoro_id} lavoro={l} onCambiato={carica} />
                  ))}
                </div>
              ) : (
                <p className="text-ink-2">
                  Nessun lavoro organizzato. Quando la conferma d'ordine dice la settimana di arrivo, scegli qui i giorni e la squadra.
                </p>
              )}
            </Riquadro>
          ) : null}
          <Riquadro titolo="Appuntamenti" testId="cliente-appuntamenti">
            <Appuntamenti appuntamenti={scheda.appuntamenti} />
          </Riquadro>
          <AssistenzeCliente cliente={c} />
        </div>

        <div className="grid content-start gap-card">
          <Riquadro titolo="Contatti" testId="cliente-contatti">
            <ul className="grid">
              <li className="flex min-h-11 items-center gap-3 border-b border-line-subtle" data-testid="cliente-telefono">
                <Phone className="size-4 shrink-0 text-ink-3" aria-hidden />
                {c.telefono ? (
                  <a href={`tel:${c.telefono_e164 || c.telefono}`} className="inline-flex min-h-11 flex-1 items-center font-semibold text-brand-ink hover:underline">
                    {c.telefono}
                  </a>
                ) : (
                  <span className="text-ink-2">nessun telefono</span>
                )}
              </li>
              <li className="flex min-h-11 items-center gap-3 border-b border-line-subtle">
                <Mail className="size-4 shrink-0 text-ink-3" aria-hidden />
                {c.email ? (
                  <a href={`mailto:${c.email}`} className="inline-flex min-h-11 min-w-0 flex-1 items-center truncate font-semibold text-brand-ink hover:underline">
                    {c.email}
                  </a>
                ) : (
                  <span className="text-ink-2">nessuna email</span>
                )}
              </li>
              {c.citta ? (
                <li className="flex min-h-11 items-center gap-3 border-b border-line-subtle">
                  <MapPin className="size-4 shrink-0 text-ink-3" aria-hidden />
                  <span className="text-ink">{[c.citta, c.provincia ? `(${c.provincia})` : null].filter(Boolean).join(" ")}</span>
                </li>
              ) : null}
              <li className="flex min-h-11 items-center gap-3 border-b border-line-subtle">
                <UserRound className="size-4 shrink-0 text-ink-3" aria-hidden />
                <span className="text-ink">{c.venditore ? `La segue ${c.venditore}` : "Nessuno lo segue"}</span>
              </li>
              <li className="grid gap-micro border-b border-line-subtle py-2" data-testid="cliente-fonte">
                <span className="text-small text-ink-2">Da dove arriva</span>
                <span className="flex flex-wrap items-center gap-x-inside">
                  <span className="text-ink">{c.fonte || <span className="text-ink-2">non indicata</span>}</span>
                  <button
                    type="button"
                    onClick={() => setFonti(true)}
                    className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 font-semibold text-brand-ink hover:underline md:min-h-8"
                    data-testid="cliente-fonti-modifica"
                  >
                    {c.fonte_automatica ? "Aggiungi" : c.fonte ? "Modifica" : "Indica"}
                  </button>
                </span>
              </li>
              <li className="grid gap-micro py-2" data-testid="cliente-consenso-wa">
                <span className="text-small text-ink-2">WhatsApp di marketing</span>
                {c.consenso_wa_marketing_at ? (
                  <span className="flex flex-wrap items-center gap-x-inside">
                    <span className="text-ink">sì, dal {giorno(c.consenso_wa_marketing_at)}</span>
                    <button
                      type="button"
                      onClick={ritiraConsenso}
                      className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 font-semibold text-brand-ink hover:underline md:min-h-8"
                      data-testid="cliente-ritira-consenso"
                    >
                      Ritira
                    </button>
                  </span>
                ) : (
                  <span className="text-ink">no</span>
                )}
              </li>
            </ul>
          </Riquadro>
          <Riquadro titolo="WhatsApp" testId="cliente-whatsapp">
            {scheda.conversazioni.length ? (
              <ul className="-mx-inside grid">
                {scheda.conversazioni.map((w) => (
                  <li key={w.wa_id}>
                    <Link
                      to={`/messaggi?contatto=${w.wa_id}`}
                      data-testid={`cliente-conversazione-${w.wa_id}`}
                      className="flex min-h-12 items-center gap-group rounded-sm px-inside py-2 transition-colors duration-150 ease-enter hover:bg-surface-2"
                    >
                      <MessageCircle className="size-4 shrink-0 text-ink-3" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-ink-strong">{w.profile_name || `+${w.wa_id}`}</p>
                        <p className="text-small text-ink-2">
                          {w.last_inbound_at ? `ultimo messaggio ${giornoOra(w.last_inbound_at)}` : "nessun messaggio ricevuto"}
                        </p>
                      </div>
                      <ChevronRight className="size-4 shrink-0 text-ink-3" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-ink-2">Nessuna conversazione agganciata.</p>
            )}
          </Riquadro>
          {scheda.prenotazioni?.length ? <DallaLanding prenotazioni={scheda.prenotazioni} cliente={c} /> : null}
          {c.progetto || c.budget_fascia || c.note ? (
            <Riquadro titolo="Il progetto" testId="cliente-progetto">
              <dl className="grid">
                {c.progetto ? <Dato etichetta="Cosa deve arredare">{c.progetto}</Dato> : null}
                {c.budget_fascia ? <Dato etichetta="Budget dichiarato">{c.budget_fascia}</Dato> : null}
                {c.note ? <Dato etichetta="Note">{c.note}</Dato> : null}
              </dl>
            </Riquadro>
          ) : null}
          <ArchivioDocumenti clienteId={c.id} generati={scheda.documenti} onCambiato={carica} chi={{ id: profile?.id, ruolo: profile?.ruolo, contratti: voci.includes("contratti") }} />
        </div>
      </div>
    </section>
  );
}
